import React, { useState, useEffect, useMemo } from 'react';
import { 
    FaSmile, FaMeh, FaFrown, FaStar, FaChartBar, FaWrench, 
    FaCommentDots, FaUser, FaPlus, FaTrash, FaInbox, FaRegCommentAlt 
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../services/api';
import './GestaoSatisfacao.css';

// Componente para o "Relatório por Atendente"
const RelatorioAgentes = ({ data }) => {
    if (!data.length) {
        return (
            <div className="gsat-empty-state">
                <FaInbox className="gsat-empty-icon" />
                <p>Nenhum dado de agente disponível para o período selecionado.</p>
            </div>
        );
    }
    return (
        <div className="gsat-table-container">
            <table className="gsat-table">
                <thead>
                    <tr>
                        <th>Atendente</th>
                        <th className="gsat-text-center">Total de Avaliações</th>
                        <th className="gsat-text-center">Média (1-5)</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map(agente => (
                        <tr key={agente.atribuido_a_id || Math.random()}>
                            <td>
                                <div className="gsat-agent-info">
                                    <div className="gsat-avatar-circle">
                                        {agente.atendente_nome ? agente.atendente_nome.charAt(0).toUpperCase() : '?'}
                                    </div>
                                    <span className="gsat-agent-name">{agente.atendente_nome || 'Não atribuído'}</span>
                                </div>
                            </td>
                            <td className="gsat-text-center">
                                <span className="gsat-data-value">{agente.total_avaliacoes}</span>
                            </td>
                            <td className="gsat-text-center">
                                <div className="gsat-rating-badge">
                                    <FaStar className="gsat-star-icon" />
                                    <span>{parseFloat(agente.media_nota).toFixed(1)}</span>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// Componente para a "Barra de Humor"
const BarraDeHumor = ({ stats }) => {
    const safeStats = stats || { total: 0, notas: {} };
    const total = safeStats.total || 1;
    const felizPerc = ((safeStats.notas['5'] || 0) + (safeStats.notas['4'] || 0)) / total * 100;
    const neutroPerc = (safeStats.notas['3'] || 0) / total * 100;
    const tristePerc = ((safeStats.notas['2'] || 0) + (safeStats.notas['1'] || 0)) / total * 100;

    return (
        <div className="gsat-mood-wrapper">
            <div className="gsat-mood-track">
                <div className="gsat-mood-segment gsat-bg-success" style={{ width: `${felizPerc}%` }} title={`Positivo: ${felizPerc.toFixed(1)}%`} />
                <div className="gsat-mood-segment gsat-bg-warning" style={{ width: `${neutroPerc}%` }} title={`Neutro: ${neutroPerc.toFixed(1)}%`} />
                <div className="gsat-mood-segment gsat-bg-danger" style={{ width: `${tristePerc}%` }} title={`Negativo: ${tristePerc.toFixed(1)}%`} />
            </div>
            <div className="gsat-mood-legend">
                <div className="gsat-legend-item">
                    <FaSmile className="gsat-text-success" size={16} /> 
                    <span>Positivo ({felizPerc.toFixed(0)}%)</span>
                </div>
                <div className="gsat-legend-item">
                    <FaMeh className="gsat-text-warning" size={16} /> 
                    <span>Neutro ({neutroPerc.toFixed(0)}%)</span>
                </div>
                <div className="gsat-legend-item">
                    <FaFrown className="gsat-text-danger" size={16} /> 
                    <span>Negativo ({tristePerc.toFixed(0)}%)</span>
                </div>
            </div>
        </div>
    );
};

// Componente para "Feed de Comentários"
const RelatorioComentarios = ({ data }) => {
    if (!data.length) {
        return (
            <div className="gsat-empty-state">
                <FaRegCommentAlt className="gsat-empty-icon" />
                <p>Nenhum comentário de satisfação encontrado para este período.</p>
            </div>
        );
    }
    
    const formatDate = (dateString) => {
        const options = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };
        return new Date(dateString).toLocaleDateString('pt-BR', options);
    };

    return (
        <div className="gsat-comments-grid">
            {data.map(item => (
                <div key={item.id} className="gsat-comment-card">
                    <div className="gsat-comment-header">
                        <div className="gsat-comment-author">
                            <div className="gsat-avatar-small"><FaUser size={12} /></div>
                            <div className="gsat-author-info">
                                <strong className="gsat-author-name">{item.usuario_nome}</strong>
                                <span className="gsat-author-date">{formatDate(item.criado_em)}</span>
                            </div>
                        </div>
                        <div className={`gsat-comment-rating-tag gsat-rating-${Math.round(item.nota)}`}>
                            <FaStar size={12} /> {parseFloat(item.nota).toFixed(1)}
                        </div>
                    </div>
                    <div className="gsat-comment-body">
                        <p>"{item.comentario}"</p>
                    </div>
                    <div className="gsat-comment-footer">
                        <span className="gsat-ticket-ref">Referência: Chamado #{item.chamado_id}</span>
                    </div>
                </div>
            ))}
        </div>
    );
};

// --- COMPONENTE DE CONFIGURAÇÃO DE PERGUNTAS ---
const ConfiguracaoPerguntas = () => {
    const [perguntas, setPerguntas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const fetchPerguntas = async () => {
            setLoading(true);
            try {
                const { data } = await api.get('/api/admin/suporte/satisfacao/perguntas');
                const perguntasFormatadas = data.map(p => ({
                    ...p,
                    opcoes: p.opcoes ? p.opcoes.join(', ') : ''
                }));
                setPerguntas(perguntasFormatadas.sort((a, b) => a.ordem - b.ordem));
            } catch (error) {
                toast.error('Falha ao carregar configurações de perguntas.');
            } finally {
                setLoading(false);
            }
        };
        fetchPerguntas();
    }, []);

    const handleAddPergunta = (tipo) => {
        const novaPergunta = {
            id: `new_${Date.now()}`,
            texto: '',
            tipo: tipo,
            ordem: perguntas.length + 1,
            opcoes: tipo === 'lista_suspensa' ? '' : null,
            obrigatoria: true,
        };
        setPerguntas([...perguntas, novaPergunta]);
    };

    const handleUpdatePergunta = (id, campo, valor) => {
        setPerguntas(perguntas.map(p => p.id === id ? { ...p, [campo]: valor } : p));
    };

    const handleDeletePergunta = (id) => {
        const novasPerguntas = perguntas.filter(p => p.id !== id);
        novasPerguntas.forEach((p, index) => p.ordem = index + 1);
        setPerguntas(novasPerguntas);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        for (const p of perguntas) {
            if (!p.texto.trim()) {
                toast.warn(`O enunciado da pergunta #${p.ordem} não pode estar vazio.`);
                return;
            }
            if (p.tipo === 'lista_suspensa') {
                const opcoesArray = p.opcoes ? p.opcoes.split(',').map(opt => opt.trim()).filter(Boolean) : [];
                if (opcoesArray.length < 2) {
                    toast.warn(`A pergunta #${p.ordem} deve conter pelo menos 2 opções separadas por vírgula.`);
                    return;
                }
            }
        }
        
        setIsSaving(true);
        try {
            const payload = perguntas.map((p, index) => ({
                ...p,
                id: p.id.toString().startsWith('new_') ? null : p.id,
                ordem: index + 1,
                opcoes: p.tipo === 'lista_suspensa' ? (p.opcoes ? p.opcoes.split(',').map(opt => opt.trim()).filter(Boolean) : []) : null
            }));
            
            const { data } = await api.post('/api/admin/suporte/satisfacao/perguntas', { perguntas: payload });
            
            const perguntasFormatadas = data.map(p => ({
                ...p,
                opcoes: p.opcoes ? p.opcoes.join(', ') : ''
            }));
            setPerguntas(perguntasFormatadas.sort((a, b) => a.ordem - b.ordem));
            toast.success('Pesquisa configurada com sucesso!');
        } catch (error) {
            toast.error('Falha ao salvar a configuração da pesquisa.');
        } finally {
            setIsSaving(false);
        }
    };

    const renderTipoTag = (tipo) => {
        const map = { 'nota_5': 'Avaliação (1-5)', 'texto_aberto': 'Texto Livre', 'lista_suspensa': 'Múltipla Escolha' };
        return <span className={`gsat-type-tag gsat-type-${tipo}`}>{map[tipo] || tipo}</span>;
    };

    if (loading) return <div className="gsat-loading-state">Carregando editor de pesquisa...</div>;

    return (
        <form onSubmit={handleSave} className="gsat-config-wrapper">
            <div className="gsat-questions-builder">
                {perguntas.length === 0 && (
                    <div className="gsat-empty-state">
                        <FaWrench className="gsat-empty-icon" />
                        <p>A pesquisa de satisfação não possui perguntas. Adicione-as abaixo.</p>
                    </div>
                )}

                {perguntas.map((p) => (
                    <div key={p.id} className="gsat-builder-card">
                        <div className="gsat-builder-header">
                            <div className="gsat-builder-meta">
                                <span className="gsat-question-number">Pergunta {p.ordem}</span>
                                {renderTipoTag(p.tipo)}
                            </div>
                            <button type="button" className="gsat-btn-icon-trash" onClick={() => handleDeletePergunta(p.id)} title="Excluir pergunta">
                                <FaTrash size={14} />
                            </button>
                        </div>
                        
                        <div className="gsat-form-group">
                            <label className="gsat-form-label">Enunciado da Pergunta</label>
                            <input
                                type="text"
                                className="gsat-input-control"
                                value={p.texto}
                                placeholder="Ex: Como você avalia a clareza e a rapidez do nosso suporte?"
                                onChange={(e) => handleUpdatePergunta(p.id, 'texto', e.target.value)}
                            />
                        </div>
                        
                        {p.tipo === 'lista_suspensa' && (
                            <div className="gsat-form-group">
                                <label className="gsat-form-label">Opções de Resposta <span className="gsat-text-hint">(separe os itens por vírgula)</span></label>
                                <input
                                    type="text"
                                    className="gsat-input-control"
                                    value={p.opcoes || ''}
                                    placeholder="Ex: Muito Satisfeito, Satisfeito, Insatisfeito"
                                    onChange={(e) => handleUpdatePergunta(p.id, 'opcoes', e.target.value)}
                                />
                            </div>
                        )}
                        
                        <div className="gsat-form-toggle">
                            <label className="gsat-checkbox-container">
                                <input
                                    type="checkbox"
                                    checked={p.obrigatoria}
                                    onChange={(e) => handleUpdatePergunta(p.id, 'obrigatoria', e.target.checked)}
                                />
                                <span className="gsat-checkbox-custom"></span>
                                <span className="gsat-checkbox-label">Tornar resposta obrigatória</span>
                            </label>
                        </div>
                    </div>
                ))}
            </div>

            <div className="gsat-config-footer">
                <div className="gsat-add-actions">
                    <span className="gsat-actions-label">Adicionar nova:</span>
                    <div className="gsat-btn-group">
                        <button type="button" className="gsat-btn gsat-btn-outline" onClick={() => handleAddPergunta('nota_5')}><FaPlus size={12}/> Nota</button>
                        <button type="button" className="gsat-btn gsat-btn-outline" onClick={() => handleAddPergunta('texto_aberto')}><FaPlus size={12}/> Texto Livre</button>
                        <button type="button" className="gsat-btn gsat-btn-outline" onClick={() => handleAddPergunta('lista_suspensa')}><FaPlus size={12}/> Múltipla Escolha</button>
                    </div>
                </div>
                <button type="submit" className="gsat-btn gsat-btn-primary" disabled={isSaving}>
                    {isSaving ? 'Salvando...' : 'Salvar Pesquisa'}
                </button>
            </div>
        </form>
    );
};

// --- COMPONENTE PRINCIPAL ---
export default function GestaoSatisfacao() {
    const [view, setView] = useState('relatorios'); 
    const [periodo, setPeriodo] = useState('30d'); 
    const [stats, setStats] = useState({ total: 0, media: 0, taxa_resposta: 0, notas: {} });
    const [agentes, setAgentes] = useState([]);
    const [comentarios, setComentarios] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const params = { periodo };
                const [statsRes, agentesRes, comentariosRes] = await Promise.all([
                    api.get('/api/admin/suporte/satisfacao/stats', { params }),
                    api.get('/api/admin/suporte/satisfacao/por-atendente', { params }),
                    api.get('/api/admin/suporte/satisfacao/comentarios', { params })
                ]);
                
                setStats(statsRes.data || {});
                setAgentes(agentesRes.data || []);
                setComentarios(comentariosRes.data || []);
            } catch (error) {
                console.error(error);
                toast.error("Erro ao carregar dados de satisfação.");
            } finally {
                setLoading(false);
            }
        };

        if (view !== 'configuracoes') fetchData();
        else setLoading(false);
    }, [periodo, view]);

    const kpiData = useMemo(() => ({
        media: parseFloat(stats.media || 0).toFixed(1),
        total: stats.total || 0,
        taxa: parseFloat(stats.taxa_resposta || 0).toFixed(0),
    }), [stats]);

    return (
        <div className="gsat-page">
            <div className="gsat-page-header">
                <div className="gsat-header-content">
                    <h1 className="gsat-page-title">Qualidade e Satisfação (CSAT)</h1>
                    <p className="gsat-page-subtitle">Acompanhe as avaliações dos clientes e gerencie a pesquisa de satisfação.</p>
                </div>
            </div>

            <div className="gsat-toolbar">
                <div className="gsat-tabs-control">
                    <button className={`gsat-tab-btn ${view === 'relatorios' ? 'is-active' : ''}`} onClick={() => setView('relatorios')}>
                        <FaChartBar size={14} /> Relatórios
                    </button>
                    <button className={`gsat-tab-btn ${view === 'comentarios' ? 'is-active' : ''}`} onClick={() => setView('comentarios')}>
                        <FaCommentDots size={14} /> Comentários
                    </button>
                    <button className={`gsat-tab-btn ${view === 'configuracoes' ? 'is-active' : ''}`} onClick={() => setView('configuracoes')}>
                        <FaWrench size={14} /> Configurações
                    </button>
                </div>

                {view !== 'configuracoes' && (
                    <div className="gsat-period-filters">
                        {['7d', '30d', '90d', 'all'].map(p => (
                            <button key={p} className={`gsat-filter-pill ${periodo === p ? 'is-active' : ''}`} onClick={() => setPeriodo(p)}>
                                {p === 'all' ? 'Todo o período' : `Últimos ${p.replace('d', '')} dias`}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="gsat-page-content">
                {view === 'relatorios' && (
                    loading ? <div className="gsat-loading-state">Atualizando métricas...</div> : (
                        <div className="gsat-animation-fade">
                            <div className="gsat-kpi-row">
                                <div className="gsat-card gsat-kpi-card">
                                    <div className="gsat-kpi-header">
                                        <span className="gsat-kpi-title">Média de Satisfação</span>
                                        <FaStar className="gsat-kpi-icon gsat-text-warning" />
                                    </div>
                                    <div className="gsat-kpi-body">
                                        <span className="gsat-kpi-value">{kpiData.media}</span>
                                        <span className="gsat-kpi-suffix">/ 5.0</span>
                                    </div>
                                </div>
                                <div className="gsat-card gsat-kpi-card">
                                    <div className="gsat-kpi-header">
                                        <span className="gsat-kpi-title">Pesquisas Respondidas</span>
                                        <FaChartBar className="gsat-kpi-icon gsat-text-primary" />
                                    </div>
                                    <div className="gsat-kpi-body">
                                        <span className="gsat-kpi-value">{kpiData.total}</span>
                                    </div>
                                </div>
                                <div className="gsat-card gsat-kpi-card">
                                    <div className="gsat-kpi-header">
                                        <span className="gsat-kpi-title">Taxa de Resposta (Engajamento)</span>
                                        <FaCommentDots className="gsat-kpi-icon gsat-text-success" />
                                    </div>
                                    <div className="gsat-kpi-body">
                                        <span className="gsat-kpi-value">{kpiData.taxa}%</span>
                                    </div>
                                </div>
                            </div>

                            <div className="gsat-card gsat-mb-xl">
                                <div className="gsat-card-header">
                                    <h3 className="gsat-card-title">Distribuição de Humor (NPS Tracker)</h3>
                                </div>
                                <div className="gsat-card-body">
                                    <BarraDeHumor stats={stats} />
                                </div>
                            </div>

                            <div className="gsat-card">
                                <div className="gsat-card-header">
                                    <h3 className="gsat-card-title">Desempenho da Equipe</h3>
                                </div>
                                <div className="gsat-card-body gsat-p-0">
                                    <RelatorioAgentes data={agentes} />
                                </div>
                            </div>
                        </div>
                    )
                )}

                {view === 'comentarios' && (
                    loading ? <div className="gsat-loading-state">Carregando feedback...</div> : (
                        <div className="gsat-animation-fade">
                            <RelatorioComentarios data={comentarios} />
                        </div>
                    )
                )}

                {view === 'configuracoes' && (
                    <div className="gsat-animation-fade">
                        <ConfiguracaoPerguntas />
                    </div>
                )}
            </div>
        </div>
    );
}